import { LuShield } from 'react-icons/lu';

import type { ArShapeFeature } from '~api/services';
import { PropertyGroup, PropertyItem } from '~components';
import { ScrollArea } from '~components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~components/ui/sheet';
import { formatEpochToUTC, formatPropertyValue } from '~utils';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type ArShapeDetailsDrawerProps = {
  /** The AR shape feature data to display */
  shape: ArShapeFeature | null;
  /** The container element to render the drawer into */
  container?: HTMLElement;
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when the drawer is closed */
  onClose: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/** Keys shown in the primary section — excluded from the "other properties" list */
const PRIMARY_KEYS = new Set([
  'AR_Type',
  'AR_Shape_Status',
  'Title',
  'Permit_Effective_Date',
  'Permit_End_Date',
  'OBJECTID',
]);

/** Keys that contain epoch timestamps and should be formatted as dates */
const DATE_KEYS = new Set([
  'Permit_Effective_Date',
  'Permit_End_Date',
  'Effective_Date',
  'End_Date',
  'Created_On',
  'Updated_On',
]);

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sheet component to display details of a selected AR permit shape.
 * Shows primary permit properties (type, status, title, dates) prominently,
 * followed by a scrollable section of additional properties.
 *
 * @example
 * ```tsx
 * <ArShapeDetailsDrawer
 *   shape={selectedArShape}
 *   container={parentContainerRef}
 *   open={arShapeDrawerOpen}
 *   onClose={() => handleDrawerClose()}
 * />
 * ```
 */
const ArShapeDetailsDrawer = ({ shape, container, open, onClose }: ArShapeDetailsDrawerProps) => {
  const otherProperties = shape ? Object.entries(shape.properties).filter(([key]) => !PRIMARY_KEYS.has(key)) : [];

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        portalContainer={container ?? document.body}
        side="inline-end"
        strategy="absolute"
        showOverlay={false}
        className="grow border-t-0"
        data-open={open}
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className="flex gap-2">
            <LuShield className="rounded-full bg-orange-500/10 p-1 text-orange-500" size={32} />
            {shape?.properties.Title || 'AR Permit Details'}
          </SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="h-full w-full" type="auto">
            {shape && (
              <div className="space-y-6 p-6">
                {/* Primary properties */}
                <PropertyGroup withBorder>
                  <PropertyItem label="Type" value={shape.properties.AR_Type ?? '-'} variant="featured" />
                  <PropertyItem label="Status" value={shape.properties.AR_Shape_Status ?? '-'} variant="featured" />
                  <PropertyItem
                    label="Permit Effective Date"
                    value={formatEpochToUTC(shape.properties.Permit_Effective_Date)}
                    variant="featured"
                  />
                  <PropertyItem
                    label="Permit End Date"
                    value={formatEpochToUTC(shape.properties.Permit_End_Date)}
                    variant="featured"
                  />
                </PropertyGroup>

                {/* Additional properties */}
                {otherProperties.length > 0 && (
                  <PropertyGroup>
                    {otherProperties.map(([key, value]) => (
                      <PropertyItem
                        key={key}
                        label={key}
                        value={formatPropertyValue(key, value, DATE_KEYS)}
                        contained
                      />
                    ))}
                  </PropertyGroup>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { ArShapeDetailsDrawer };
